import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { RotateCcw } from "lucide-react";
import {
  HostNavigation,
  PageNavigation,
  PublicHostLink,
  SuccessToast,
} from "./components/AppNavigation";
import { HostAccessPanel, type HostAccessPanelCopy } from "./components/HostAccessPanel";
import { LanguagePicker } from "./components/LanguagePicker";
import { ListeningScreen } from "./components/ListeningScreen";
import {
  MeetingArchive,
  MeetingTranscriptView,
  type MeetingArchiveCopy,
} from "./components/MeetingArchive";
import {
  RecordingStatusStrip,
  type RecordingStatusStripCopy,
  type RecordingStatus as RecordingStatusView,
} from "./components/RecordingStatusStrip";
import { SetupScreen } from "./components/SetupScreen";
import { useAudioDevices, type UseAudioDevicesResult } from "./hooks/useAudioDevices";
import {
  useMeetingRecording,
  type MeetingRecordingErrorCode,
  type UseMeetingRecordingResult,
} from "./hooks/useMeetingRecording";
import {
  useRealtimeTranslation,
  type UseRealtimeTranslationResult,
} from "./hooks/useRealtimeTranslation";
import { applyDocumentMetadata } from "./shared/documentMetadata";
import { isTargetLanguage, type TargetLanguage } from "./shared/languages";
import { UI_COPY, type UiCopyKey } from "./shared/uiCopy";
import {
  currentSession as fetchCurrentSession,
  login,
  logout,
  type AuthSession,
} from "./lib/meetingApi";
import "./index.css";

export type InterpreterExperienceProps = {
  audioDevices: UseAudioDevicesResult;
  realtime: UseRealtimeTranslationResult;
  translatedAudioRef?: RefObject<HTMLAudioElement | null>;
  startTimeoutMs?: number;
  setupFooter?(language: TargetLanguage): ReactNode;
  hostRecording?: {
    canStart: boolean;
    confirmStopMessage(language: TargetLanguage): string;
    meetingTitle: string;
    recording: UseMeetingRecordingResult;
    setupSupplement(language: TargetLanguage): ReactNode;
    setupFooter?(language: TargetLanguage): ReactNode;
    listeningSupplement(language: TargetLanguage): ReactNode;
    onFinalized?(meetingId: string): void;
  };
};

type StartPhase = "idle" | "microphone" | "connection";

const DEFAULT_START_TIMEOUT_MS = 20_000;

export function InterpreterExperience({
  audioDevices,
  realtime,
  translatedAudioRef: sharedTranslatedAudioRef,
  startTimeoutMs = DEFAULT_START_TIMEOUT_MS,
  setupFooter,
  hostRecording,
}: InterpreterExperienceProps) {
  const [language, setLanguage] = useState<TargetLanguage>("en");
  const [listening, setListening] = useState(false);
  const [textVisible, setTextVisible] = useState(true);
  const [audioRequested, setAudioRequested] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [volume, setVolume] = useState(0.9);
  const [localError, setLocalError] = useState<string | null>(null);
  const [startPhase, setStartPhase] = useState<StartPhase>("idle");
  const internalTranslatedAudioRef = useRef<HTMLAudioElement>(null);
  const translatedAudioRef = sharedTranslatedAudioRef ?? internalTranslatedAudioRef;
  const outputTestAudioRef = useRef<HTMLAudioElement>(null);
  const sourceStreamRef = useRef<MediaStream | null>(null);
  const startOperationRef = useRef(0);
  const startInFlightRef = useRef(false);
  const audioIntentGenerationRef = useRef(0);
  const audioRequestedRef = useRef(false);
  const outputUiGenerationRef = useRef(0);
  const audioRouteAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const nextStream = audioDevices.microphoneStream;
    if (!listening || !nextStream || sourceStreamRef.current === nextStream) return;
    sourceStreamRef.current = nextStream;
    void realtime.replaceMicrophone(nextStream).catch(() => setLocalError("no-microphone"));
  }, [audioDevices.microphoneStream, listening, realtime.replaceMicrophone]);

  useEffect(() => {
    if (translatedAudioRef.current) translatedAudioRef.current.volume = volume;
  }, [translatedAudioRef, volume]);

  useEffect(() => () => {
    startOperationRef.current += 1;
    audioIntentGenerationRef.current += 1;
    outputUiGenerationRef.current += 1;
    audioRouteAbortRef.current?.abort();
    audioRouteAbortRef.current = null;
    realtime.stop();
    audioDevices.releaseMicrophone();
  }, [audioDevices.releaseMicrophone, realtime.stop]);

  const selectOutputBeforeAudio = async (signal?: AbortSignal) => {
    const audioElement = translatedAudioRef.current;
    if (!audioElement) throw new Error("audio-output");
    await audioDevices.selectOutput(audioDevices.selectedOutputId, audioElement, signal);
  };

  const cancelAudioRoute = () => {
    audioRouteAbortRef.current?.abort();
    audioRouteAbortRef.current = null;
  };

  const handleAudioEnabledChange = async (value: boolean) => {
    cancelAudioRoute();
    const intent = ++audioIntentGenerationRef.current;
    audioRequestedRef.current = value;
    setAudioRequested(value);
    if (!value) {
      setAudioEnabled(false);
      realtime.setAudioEnabled(false);
      return;
    }
    if (!listening) {
      setAudioEnabled(false);
      realtime.setAudioEnabled(false);
      return;
    }
    const controller = new AbortController();
    audioRouteAbortRef.current = controller;
    try {
      await selectOutputBeforeAudio(controller.signal);
      if (
        audioIntentGenerationRef.current !== intent ||
        !audioRequestedRef.current
      ) return;
      setLocalError(null);
      setAudioEnabled(true);
      realtime.setAudioEnabled(true);
    } catch {
      if (
        audioIntentGenerationRef.current !== intent ||
        !audioRequestedRef.current
      ) return;
      audioIntentGenerationRef.current += 1;
      audioRequestedRef.current = false;
      setAudioRequested(false);
      setAudioEnabled(false);
      realtime.setAudioEnabled(false);
      setLocalError("audio-output");
    } finally {
      if (audioRouteAbortRef.current === controller) audioRouteAbortRef.current = null;
    }
  };

  const handleStart = async () => {
    if (startInFlightRef.current) return;
    if (hostRecording && !hostRecording.canStart) return;
    startInFlightRef.current = true;
    const operation = ++startOperationRef.current;
    setLocalError(null);
    setStartPhase("microphone");
    let stream: MediaStream;
    const microphoneWaitTimer = window.setTimeout(() => {
      if (startOperationRef.current === operation) setLocalError("microphone-timeout");
    }, startTimeoutMs);
    try {
      stream = await audioDevices.requestPermission();
    } catch (caught) {
      if (startOperationRef.current === operation) {
        startInFlightRef.current = false;
        setStartPhase("idle");
        setLocalError(microphoneErrorCode(caught));
      }
      return;
    } finally {
      window.clearTimeout(microphoneWaitTimer);
    }
    if (startOperationRef.current !== operation) return;
    setLocalError(null);
    sourceStreamRef.current = stream;
    setStartPhase("connection");
    let recordingStarted = false;

    if (hostRecording) {
      try {
        await hostRecording.recording.start(stream, hostRecording.meetingTitle);
        recordingStarted = true;
        setListening(true);
      } catch {
        if (startOperationRef.current === operation) {
          audioDevices.releaseMicrophone();
          sourceStreamRef.current = null;
          startInFlightRef.current = false;
          setStartPhase("idle");
        }
        return;
      }
    }

    if (audioRequestedRef.current) {
      const audioIntent = audioIntentGenerationRef.current;
      cancelAudioRoute();
      const controller = new AbortController();
      audioRouteAbortRef.current = controller;
      try {
        await withTimeout(
          selectOutputBeforeAudio(controller.signal),
          startTimeoutMs,
          "audio-output-timeout",
        );
        if (startOperationRef.current !== operation) return;
        if (
          audioIntentGenerationRef.current === audioIntent &&
          audioRequestedRef.current
        ) {
          setAudioEnabled(true);
          realtime.setAudioEnabled(true);
        } else {
          setAudioEnabled(false);
          realtime.setAudioEnabled(false);
        }
      } catch {
        controller.abort();
        if (startOperationRef.current !== operation) return;
        if (
          audioIntentGenerationRef.current !== audioIntent ||
          !audioRequestedRef.current
        ) {
          setAudioEnabled(false);
          realtime.setAudioEnabled(false);
        } else {
          audioIntentGenerationRef.current += 1;
          audioRequestedRef.current = false;
          setAudioRequested(false);
          setAudioEnabled(false);
          realtime.setAudioEnabled(false);
          setLocalError("audio-output");
        }
      } finally {
        if (audioRouteAbortRef.current === controller) audioRouteAbortRef.current = null;
      }
    } else {
      realtime.setAudioEnabled(false);
    }

    if (startOperationRef.current !== operation) return;
    try {
      await withTimeout(
        realtime.start(language, stream),
        startTimeoutMs,
        "connection-timeout",
      );
      if (startOperationRef.current === operation) {
        startInFlightRef.current = false;
        setStartPhase("idle");
        setListening(true);
      }
    } catch (caught) {
      if (startOperationRef.current === operation) {
        realtime.stop();
        startInFlightRef.current = false;
        setStartPhase("idle");
        setLocalError(connectionErrorCode(caught));
        if (!recordingStarted) {
          audioDevices.releaseMicrophone();
          sourceStreamRef.current = null;
        } else {
          setListening(true);
        }
      }
    }
  };

  const clearLiveSession = () => {
    startInFlightRef.current = false;
    startOperationRef.current += 1;
    audioIntentGenerationRef.current += 1;
    outputUiGenerationRef.current += 1;
    cancelAudioRoute();
    realtime.stop();
    audioDevices.releaseMicrophone();
    sourceStreamRef.current = null;
    setListening(false);
    setAudioEnabled(false);
    setStartPhase("idle");
    setLocalError(null);
  };

  const endSession = async () => {
    if (hostRecording?.recording.status === "ready") {
      clearLiveSession();
      hostRecording.recording.reset();
      return;
    }

    if (hostRecording?.recording.meetingId) {
      const completedMeetingId = hostRecording.recording.meetingId;
      if (!window.confirm(hostRecording.confirmStopMessage(language))) return;
      startInFlightRef.current = false;
      startOperationRef.current += 1;
      cancelAudioRoute();
      realtime.stop();
      try {
        await hostRecording.recording.stopAndFinalize();
        clearLiveSession();
        hostRecording.recording.reset();
        hostRecording.onFinalized?.(completedMeetingId);
      } catch {
        audioDevices.releaseMicrophone();
        sourceStreamRef.current = null;
        setAudioEnabled(false);
        setStartPhase("idle");
        setListening(true);
      }
      return;
    }

    clearLiveSession();
  };

  const handleLanguageChange = (value: TargetLanguage) => {
    clearLiveSession();
    setLanguage(value);
  };

  const handleRetry = async () => {
    setLocalError(null);
    try {
      await realtime.retry();
      if (sourceStreamRef.current) setListening(true);
    } catch {
      setLocalError("connection");
    }
  };

  const handleInputChange = async (deviceId: string) => {
    if (hostRecording?.recording.isActive) return;
    try {
      await audioDevices.selectInput(deviceId);
      setLocalError(null);
    } catch {
      setLocalError("no-microphone");
    }
  };

  const handleOutputChange = async (deviceId: string) => {
    const outputIntent = ++outputUiGenerationRef.current;
    const audioElement = translatedAudioRef.current;
    if (!audioElement) return;
    try {
      await audioDevices.selectOutput(deviceId, audioElement);
      if (outputUiGenerationRef.current === outputIntent) setLocalError(null);
    } catch {
      if (outputUiGenerationRef.current === outputIntent) setLocalError("audio-output");
    }
  };

  const handleTestOutput = async () => {
    const outputIntent = outputUiGenerationRef.current;
    const audioElement = outputTestAudioRef.current;
    if (!audioElement) return;
    try {
      await audioDevices.testOutput(audioElement);
      if (outputUiGenerationRef.current === outputIntent) setLocalError(null);
    } catch {
      if (outputUiGenerationRef.current === outputIntent) setLocalError("audio-output");
    }
  };

  const handleTestInput = async () => {
    try {
      await audioDevices.testInput();
      setLocalError(null);
    } catch {
      setLocalError("no-microphone");
    }
  };

  const errorCode = localError ?? audioDevices.error ?? realtime.error;
  const errorMessage = errorCode ? UI_COPY[language][errorCopyKey(errorCode)] : null;
  const devices = {
    inputs: audioDevices.inventory.inputs,
    outputs: audioDevices.inventory.outputs,
    selectedInputId: audioDevices.selectedInputId,
    selectedOutputId: audioDevices.selectedOutputId,
    outputSelectionSupported: audioDevices.outputSelectionSupported,
    inputLevel: audioDevices.inputLevel,
    onInputChange: (deviceId: string) => void handleInputChange(deviceId),
    onOutputChange: (deviceId: string) => void handleOutputChange(deviceId),
    onTestInput: () => void handleTestInput(),
    onTestOutput: () => void handleTestOutput(),
  };

  return (
    <>
      <audio
        data-translated-audio
        ref={translatedAudioRef}
        hidden
        autoPlay
        muted={!listening || !audioEnabled}
      />
      <audio data-output-test-audio ref={outputTestAudioRef} hidden />
      {listening ? (
        <ListeningScreen
          language={language}
          transcript={realtime.transcript}
          status={realtime.status}
          microphoneEnabled={realtime.microphoneEnabled}
          audioEnabled={audioRequested}
          textVisible={textVisible}
          volume={volume}
          devices={devices}
          errorMessage={errorMessage}
          recordingActive={hostRecording?.recording.isActive}
          supplement={hostRecording?.listeningSupplement(language)}
          onMicrophoneEnabledChange={realtime.setMicrophoneEnabled}
          onAudioEnabledChange={value => void handleAudioEnabledChange(value)}
          onTextVisibleChange={setTextVisible}
          onVolumeChange={setVolume}
          onChangeLanguage={() => void endSession()}
          onLeave={() => void endSession()}
          onRetry={() => void handleRetry()}
        />
      ) : (
        <SetupScreen
          language={language}
          audioEnabled={audioRequested}
          devices={devices}
          status={realtime.status}
          startPhase={startPhase}
          errorMessage={errorMessage}
          supplement={hostRecording?.setupSupplement(language)}
          footer={hostRecording?.setupFooter?.(language) ?? setupFooter?.(language)}
          startDisabled={Boolean(hostRecording && !hostRecording.canStart)}
          onLanguageChange={handleLanguageChange}
          onAudioEnabledChange={value => {
            cancelAudioRoute();
            audioIntentGenerationRef.current += 1;
            audioRequestedRef.current = value;
            setAudioRequested(value);
            if (!value) {
              setAudioEnabled(false);
              realtime.setAudioEnabled(false);
            }
          }}
          onStart={() => void handleStart()}
          onRetry={() => void handleStart()}
        />
      )}
    </>
  );
}

export function App() {
  const route = resolveAppRoute(window.location.pathname);
  if (route.kind === "host-archive") return <HostArchiveApp />;
  if (route.kind === "host-transcript") {
    return <HostArchiveApp transcriptMeetingId={route.meetingId} />;
  }
  if (route.kind === "host") return <HostApp />;
  return <ParticipantApp />;
}

function ParticipantApp() {
  const translatedAudioRef = useRef<HTMLAudioElement>(null);
  const audioDevices = useAudioDevices();
  const realtime = useRealtimeTranslation(translatedAudioRef);

  return (
    <InterpreterExperience
      audioDevices={audioDevices}
      realtime={realtime}
      translatedAudioRef={translatedAudioRef}
      setupFooter={language => (
        <PublicHostLink label={UI_COPY[language].openHostRecorder} />
      )}
    />
  );
}

function HostApp() {
  const translatedAudioRef = useRef<HTMLAudioElement>(null);
  const audioDevices = useAudioDevices();
  const realtime = useRealtimeTranslation(translatedAudioRef);
  const [session, setSession] = useState<AuthSession | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [loginPending, setLoginPending] = useState(false);
  const [logoutPending, setLogoutPending] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [meetingTitle, setMeetingTitle] = useState("");
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [completedMeetingId, setCompletedMeetingId] = useState<string | null>(null);
  const recording = useMeetingRecording(session?.csrfToken ?? null);

  useEffect(() => {
    let cancelled = false;
    void fetchCurrentSession()
      .then(current => {
        if (!cancelled) setSession(current);
      })
      .catch(() => {
        if (!cancelled) setSession(null);
      })
      .finally(() => {
        if (!cancelled) setSessionLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogin = async () => {
    if (loginPending) return;
    setLoginPending(true);
    setAuthError(false);
    try {
      const authenticated = await login(email, password);
      setSession(authenticated);
      setPassword("");
    } catch {
      setAuthError(true);
    } finally {
      setLoginPending(false);
    }
  };

  const handleLogout = async () => {
    if (!session || recording.isActive || logoutPending) return;
    setLogoutPending(true);
    setAuthError(false);
    try {
      await logout(session.csrfToken);
      setSession(null);
      recording.reset();
      setMeetingTitle("");
      setConsentConfirmed(false);
      setCompletedMeetingId(null);
    } catch {
      setAuthError(true);
    } finally {
      setLogoutPending(false);
    }
  };

  const setupSupplement = (language: TargetLanguage) => {
    const copy = UI_COPY[language];
    return (
      <>
        <HostAccessPanel
          copy={hostAccessCopy(copy)}
          errorMessage={authError ? copy.hostLoginError : null}
          {...(session ? {
            session: {
              displayName: session.user.displayName,
              email: session.user.email,
            },
            meetingTitle,
            consentConfirmed,
            controlsDisabled: recording.isActive,
            logoutPending,
            onMeetingTitleChange: setMeetingTitle,
            onConsentConfirmedChange: setConsentConfirmed,
            onLogout: () => void handleLogout(),
          } : {
            session: null,
            email,
            password,
            loginPending: loginPending || sessionLoading,
            onEmailChange: setEmail,
            onPasswordChange: setPassword,
            onLogin: () => void handleLogin(),
          })}
        />
        {session && recording.recoverableMeeting && (
          recording.status === "idle"
          || recording.status === "error"
        ) && (
          <section className="recording-recovery" role="status">
            <div>
              <h3>{copy.recordingRecoveryTitle}</h3>
              <p>
                {copy.recordingRecoveryDescription}{" "}
                <strong>{recording.recoverableMeeting.title}</strong>
              </p>
              {recording.recoverableMeetingCount > 1 && (
                <p>
                  {recording.recoverableMeetingCount} {copy.recordingRecoveryRemaining}
                </p>
              )}
            </div>
            <div className="recording-recovery__actions">
              <button
                className="secondary-button"
                type="button"
                onClick={() => void recording.recoverAndFinalize().catch(() => undefined)}
              >
                <RotateCcw size={16} aria-hidden="true" />
                {copy.recordingRecoveryAction}
              </button>
              <button
                className="secondary-button secondary-button--danger"
                type="button"
                onClick={() => {
                  if (window.confirm(copy.recordingDiscardConfirm)) {
                    void recording.discardRecoverable().catch(() => undefined);
                  }
                }}
              >
                {copy.recordingDiscardAction}
              </button>
            </div>
          </section>
        )}
        {recording.status !== "idle" && recordingStatus(language)}
      </>
    );
  };

  const recordingStatus = (language: TargetLanguage) => {
    if (recording.status === "idle") return null;
    const copy = UI_COPY[language];
    return (
      <RecordingStatusStrip
        status={recordingStatusView(recording.status)}
        copy={recordingStatusCopy(copy)}
        elapsedMs={recording.elapsedSeconds * 1000}
        pendingChunks={recording.pendingChunks}
        acknowledgedSequence={recording.lastAcknowledgedSequence}
        errorMessage={recording.error
          ? recordingErrorMessage(copy, recording.error)
          : recording.warning
            ? copy.recordingWarningStorage
            : null}
        playbackUrl={recording.playbackUrl}
        onRetry={recording.meetingId && recording.status === "error"
          ? () => void (
              recording.recoverableMeeting?.id === recording.meetingId
                ? recording.recoverAndFinalize()
                : recording.stopAndFinalize()
            ).catch(() => undefined)
          : undefined}
      />
    );
  };

  const listeningSupplement = (language: TargetLanguage) => recordingStatus(language);

  return (
    <InterpreterExperience
      audioDevices={audioDevices}
      realtime={realtime}
      translatedAudioRef={translatedAudioRef}
      hostRecording={{
        canStart: Boolean(
          session
          && meetingTitle.trim()
          && consentConfirmed
          && !recording.isActive
          && !recording.recoverableMeeting
        ),
        confirmStopMessage: language => UI_COPY[language].recordingConfirmStop,
        meetingTitle,
        recording,
        setupSupplement,
        setupFooter: language => {
          const copy = UI_COPY[language];
          return (
            <div className="host-setup-footer">
              {completedMeetingId && (
                <SuccessToast
                  message={copy.recordingSavedMessage}
                  dismissLabel={copy.dismissNotification}
                  onDismiss={() => setCompletedMeetingId(null)}
                  action={{
                    href: archiveHref(completedMeetingId, language),
                    label: copy.viewMeeting,
                  }}
                />
              )}
              <HostNavigation
                ariaLabel={copy.navigationLabel}
                translatorLabel={copy.openParticipantTranslator}
                archiveLabel={copy.openMeetingArchive}
                archiveHref={archiveHref(undefined, language)}
              />
            </div>
          );
        },
        listeningSupplement,
        onFinalized: meetingId => {
          setCompletedMeetingId(meetingId);
          setMeetingTitle("");
          setConsentConfirmed(false);
        },
      }}
    />
  );
}

function HostArchiveApp({ transcriptMeetingId }: { transcriptMeetingId?: string }) {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [language, setLanguage] = useState<TargetLanguage>(routeLanguage);
  const copy = UI_COPY[language];

  useEffect(() => applyDocumentMetadata(language), [language]);

  useEffect(() => {
    let cancelled = false;
    void fetchCurrentSession()
      .then(current => {
        if (!cancelled) setSession(current);
      })
      .catch(() => {
        if (!cancelled) setSession(null);
      })
      .finally(() => {
        if (!cancelled) setSessionLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const initialMeetingId = new URLSearchParams(window.location.search).get("meeting") ?? undefined;

  return (
    <main className="standalone-shell">
      <section className="standalone-panel">
        <header className="standalone-header">
          <div className="product-mark product-mark--compact">
            <span>{copy.appName}</span>
          </div>
          <PageNavigation
            ariaLabel={copy.navigationLabel}
            backLabel={transcriptMeetingId ? copy.archiveBackToArchive : copy.backToHostRecorder}
            backHref={transcriptMeetingId
              ? archiveHref(transcriptMeetingId, language)
              : "/host"}
            homeLabel={copy.openParticipantTranslator}
          />
        </header>

        <div className="standalone-language">
          <LanguagePicker
            value={language}
            onChange={value => {
              setLanguage(value);
              updateRouteLanguage(value);
            }}
          />
        </div>

        {sessionLoading ? (
          <p className="standalone-message" role="status">{copy.archiveLoading}</p>
        ) : !session ? (
          <div className="standalone-message standalone-message--auth">
            <p>{copy.archiveSignInRequired}</p>
            <a className="secondary-button" href="/host">{copy.openHostRecorder}</a>
          </div>
        ) : transcriptMeetingId ? (
          <MeetingTranscriptView
            meetingId={transcriptMeetingId}
            language={language}
            copy={meetingArchiveCopy(copy)}
            csrfToken={session.csrfToken}
          />
        ) : (
          <MeetingArchive
            language={language}
            copy={meetingArchiveCopy(copy)}
            csrfToken={session.csrfToken}
            initialMeetingId={initialMeetingId}
          />
        )}
      </section>
    </main>
  );
}

function hostAccessCopy(copy: typeof UI_COPY.en): HostAccessPanelCopy {
  return {
    title: copy.hostAccessTitle,
    description: copy.hostAccessDescription,
    emailLabel: copy.hostEmailLabel,
    passwordLabel: copy.hostPasswordLabel,
    loginAction: copy.hostLoginAction,
    loggingInAction: copy.hostLoggingInAction,
    signedInLabel: copy.hostSignedInLabel,
    logoutAction: copy.hostLogoutAction,
    loggingOutAction: copy.hostLoggingOutAction,
    meetingTitleLabel: copy.hostMeetingTitleLabel,
    meetingTitlePlaceholder: copy.hostMeetingTitlePlaceholder,
    consentLabel: copy.hostConsentLabel,
    consentHelp: copy.hostConsentHelp,
  };
}

function recordingStatusCopy(copy: typeof UI_COPY.en): RecordingStatusStripCopy {
  return {
    preparingStatus: copy.recordingPreparingStatus,
    recordingStatus: copy.recordingActiveStatus,
    uploadingStatus: copy.recordingUploadingStatus,
    finalizingStatus: copy.recordingFinalizingStatus,
    readyStatus: copy.recordingReadyStatus,
    failedStatus: copy.recordingFailedStatus,
    elapsedLabel: copy.recordingElapsedLabel,
    pendingChunksLabel: copy.recordingPendingChunksLabel,
    acknowledgedChunkLabel: copy.recordingAcknowledgedChunkLabel,
    noAcknowledgedChunk: copy.recordingNoAcknowledgedChunk,
    playbackLabel: copy.recordingPlaybackLabel,
    retryAction: copy.recordingRetryAction,
  };
}

function meetingArchiveCopy(copy: typeof UI_COPY.en): MeetingArchiveCopy {
  return {
    title: copy.archiveTitle,
    description: copy.archiveDescription,
    refresh: copy.archiveRefresh,
    loading: copy.archiveLoading,
    loadError: copy.archiveLoadError,
    empty: copy.archiveEmpty,
    noResults: copy.archiveNoResults,
    selectPrompt: copy.archiveSelectPrompt,
    detailError: copy.archiveDetailError,
    retry: copy.retry,
    searchLabel: copy.archiveSearchLabel,
    searchPlaceholder: copy.archiveSearchPlaceholder,
    statusFilterLabel: copy.archiveStatusFilterLabel,
    statusAll: copy.archiveStatusAll,
    audioTitle: copy.archiveAudioTitle,
    audioUnavailable: copy.archiveAudioUnavailable,
    downloadAudio: copy.archiveDownloadAudio,
    processingTitle: copy.archiveProcessingTitle,
    processingQueued: copy.archiveProcessingQueued,
    processingRunning: copy.archiveProcessingRunning,
    processingReady: copy.archiveProcessingReady,
    processingFailed: copy.archiveProcessingFailed,
    processingError: copy.archiveProcessingError,
    retryProcessing: copy.archiveRetryProcessing,
    retryingProcessing: copy.archiveRetryingProcessing,
    transcriptTitle: copy.archiveTranscriptTitle,
    transcriptPending: copy.archiveTranscriptPending,
    transcriptEmpty: copy.archiveTranscriptEmpty,
    downloadTranscript: copy.archiveDownloadTranscript,
    viewTranscript: copy.archiveViewTranscript,
    backToArchive: copy.archiveBackToArchive,
    playFromTimestamp: copy.archivePlayFromTimestamp,
    renameSpeaker: copy.archiveRenameSpeaker,
    speakerNameLabel: copy.archiveSpeakerNameLabel,
    saveSpeaker: copy.archiveSaveSpeaker,
    cancel: copy.archiveCancel,
    speakerSaveError: copy.archiveSpeakerSaveError,
    summaryTitle: copy.archiveSummaryTitle,
    summaryPending: copy.archiveSummaryPending,
    summaryEmpty: copy.archiveSummaryEmpty,
    downloadSummary: copy.archiveDownloadSummary,
    overview: copy.archiveOverview,
    keyPoints: copy.archiveKeyPoints,
    decisions: copy.archiveDecisions,
    actionItems: copy.archiveActionItems,
    openQuestions: copy.archiveOpenQuestions,
    statusRecording: copy.archiveStatusRecording,
    statusFinalizing: copy.archiveStatusFinalizing,
    statusReady: copy.archiveStatusReady,
    statusFailed: copy.archiveStatusFailed,
    statusProcessing: copy.archiveStatusProcessing,
    localeEnglish: copy.archiveLocaleEnglish,
    localeIndonesian: copy.archiveLocaleIndonesian,
    localeChinese: copy.archiveLocaleChinese,
    localeThai: copy.archiveLocaleThai,
  };
}

type AppRoute =
  | { kind: "participant" }
  | { kind: "host" }
  | { kind: "host-archive" }
  | { kind: "host-transcript"; meetingId: string };

export function resolveAppRoute(pathname: string): AppRoute {
  const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  if (normalized === "/host") return { kind: "host" };
  if (normalized === "/host/archive") return { kind: "host-archive" };
  const transcriptMatch = normalized.match(/^\/host\/archive\/([^/]+)\/transcript$/);
  const encodedMeetingId = transcriptMatch?.[1];
  if (encodedMeetingId) {
    try {
      return { kind: "host-transcript", meetingId: decodeURIComponent(encodedMeetingId) };
    } catch {
      return { kind: "host-archive" };
    }
  }
  return { kind: "participant" };
}

function routeLanguage(): TargetLanguage {
  const candidate = new URLSearchParams(window.location.search).get("lang");
  return isTargetLanguage(candidate) ? candidate : "en";
}

function archiveHref(meetingId?: string, language?: TargetLanguage): string {
  const search = new URLSearchParams();
  if (meetingId) search.set("meeting", meetingId);
  if (language) search.set("lang", language);
  const query = search.toString();
  return `/host/archive${query ? `?${query}` : ""}`;
}

function updateRouteLanguage(language: TargetLanguage) {
  const url = new URL(window.location.href);
  url.searchParams.set("lang", language);
  window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
}

function recordingStatusView(
  status: UseMeetingRecordingResult["status"],
): RecordingStatusView {
  if (status === "recording") return "recording";
  if (status === "uploading" || status === "stopping") return "uploading";
  if (status === "finalizing") return "finalizing";
  if (status === "ready") return "ready";
  if (status === "error") return "failed";
  return "preparing";
}

function recordingErrorMessage(
  copy: typeof UI_COPY.en,
  error: MeetingRecordingErrorCode,
): string {
  if (error === "unsupported") return copy.recordingErrorUnsupported;
  if (error === "microphone") return copy.recordingErrorMicrophone;
  if (error === "upload") return copy.recordingErrorUpload;
  if (error === "recorder") return copy.recordingErrorRecorder;
  if (error === "no-audio") return copy.recordingErrorNoAudio;
  if (error === "finalize") return copy.recordingErrorFinalize;
  return copy.recordingErrorStart;
}

function errorCopyKey(error: string): UiCopyKey {
  if (error === "microphone-permission") return "errorMicrophonePermission";
  if (error === "microphone-timeout") return "errorMicrophoneTimeout";
  if (error === "microphone-busy") return "errorMicrophoneBusy";
  if (error === "unsupported-browser") return "errorUnsupportedBrowser";
  if (error === "offline") return "errorOffline";
  if (error === "no-microphone") return "errorNoMicrophone";
  if (error === "audio-output") return "errorAudioPlayback";
  if (error === "connection-timeout") return "errorConnectionTimeout";
  return "errorConnection";
}

class StartTimeoutError extends Error {
  constructor(readonly code: "audio-output-timeout" | "connection-timeout") {
    super(code);
  }
}

function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  code: StartTimeoutError["code"],
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new StartTimeoutError(code)), timeoutMs);
    promise.then(
      value => {
        window.clearTimeout(timer);
        resolve(value);
      },
      error => {
        window.clearTimeout(timer);
        reject(error);
      },
    );
  });
}

function microphoneErrorCode(error: unknown): string {
  if (typeof navigator !== "undefined") {
    if (navigator.onLine === false) return "offline";
    if (!navigator.mediaDevices?.getUserMedia) return "unsupported-browser";
  }
  const name = errorName(error);
  if (name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError") {
    return "microphone-permission";
  }
  if (name === "NotReadableError" || name === "AbortError") return "microphone-busy";
  return "no-microphone";
}

function connectionErrorCode(error: unknown): string {
  if (error instanceof StartTimeoutError) return error.code;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return "offline";
  return "connection";
}

function errorName(error: unknown): string {
  return error && typeof error === "object" && "name" in error
    ? String((error as { name: unknown }).name)
    : "";
}

export default App;
